<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import {
  listRegistrations,
  approveCert,
  rejectCert,
  fetchImageBlob,
  type RegistrationItem,
  type RegistrationFilter,
} from '../api';

const tab = ref<RegistrationFilter>('PENDING');
const loading = ref(false);
const list = ref<RegistrationItem[]>([]);
const total = ref(0);
const current = ref<RegistrationItem | null>(null);
const imgUrls = ref<string[]>([]);
const imgLoading = ref(false);

async function reload() {
  loading.value = true;
  try {
    const r = await listRegistrations(tab.value);
    list.value = r.items;
    total.value = r.total;
    current.value = null;
    imgUrls.value = [];
  } catch (e) {
    ElMessage.error(normalizeError(e));
  } finally {
    loading.value = false;
  }
}

async function openDetail(item: RegistrationItem) {
  current.value = item;
  imgUrls.value = [];
  if (!item.latestCert) return;
  imgLoading.value = true;
  try {
    const urls: string[] = [];
    for (const f of item.latestCert.fileUrls) {
      urls.push(await fetchImageBlob(f));
    }
    imgUrls.value = urls;
  } catch (e) {
    ElMessage.error(`图加载失败:${normalizeError(e)}`);
  } finally {
    imgLoading.value = false;
  }
}

async function onApprove() {
  const item = current.value;
  if (!item?.latestCert) return;
  try {
    await ElMessageBox.confirm(
      `确认通过「${item.latestCert.schoolName}」${item.latestCert.enrollYear}届 的身份审核?`,
      '审核通过',
      { confirmButtonText: '通过', cancelButtonText: '取消', type: 'success' },
    );
  } catch {
    return;
  }
  try {
    await approveCert(item.latestCert.id);
    ElMessage.success('已通过,用户获得 MBA 圈友身份');
    await reload();
  } catch (e) {
    ElMessage.error(normalizeError(e));
  }
}

async function onReject() {
  const item = current.value;
  if (!item?.latestCert) return;
  try {
    const { value } = await ElMessageBox.prompt('请填写驳回原因(用户会看到)', '驳回审核', {
      inputType: 'textarea',
      inputPlaceholder: '如:证件模糊,学校名称与录取通知书不一致',
      inputValidator: (v) => (v && v.trim().length >= 2 ? true : '至少 2 字'),
      confirmButtonText: '驳回',
      cancelButtonText: '取消',
    });
    if (!value) return;
    await rejectCert(item.latestCert.id, value.trim());
    ElMessage.success('已驳回,用户可查看原因并重新提交');
    await reload();
  } catch (e) {
    if ((e as { message?: string })?.message === 'cancel') return;
    ElMessage.error(normalizeError(e));
  }
}

function normalizeError(e: unknown): string {
  const err = e as { message?: string; code?: string };
  return err?.message || err?.code || '未知错误';
}

function formatTime(s: string | null): string {
  if (!s) return '-';
  return new Date(s).toLocaleString('zh-CN');
}

function openInNewTab(src: string) {
  window.open(src, '_blank');
}

function certStatusText(item: RegistrationItem): string {
  if (!item.latestCert) return '未提交证件';
  return { PENDING: '待审核', APPROVED: '已通过', REJECTED: '已驳回' }[item.latestCert.status];
}

function certStatusClass(item: RegistrationItem): string {
  if (!item.latestCert) return 'st-none';
  return {
    PENDING: 'st-pending',
    APPROVED: 'st-approved',
    REJECTED: 'st-rejected',
  }[item.latestCert.status];
}

onMounted(() => {
  reload();
});
</script>

<template>
  <div class="audit-pane">
    <el-tabs v-model="tab" @tab-change="reload" class="tabs">
      <el-tab-pane label="待审核" name="PENDING" />
      <el-tab-pane label="未提交证件" name="NONE" />
      <el-tab-pane label="已通过" name="APPROVED" />
      <el-tab-pane label="已驳回" name="REJECTED" />
      <el-tab-pane label="全部" name="ALL" />
    </el-tabs>

    <div class="layout">
      <!-- 左:注册用户列表 -->
      <div class="list" v-loading="loading">
        <div class="list-total" v-if="!loading">共 {{ total }} 名用户</div>
        <el-empty v-if="!list.length && !loading" description="暂无用户" />
        <div
          v-for="item of list"
          :key="item.user.id"
          class="list-item"
          :class="{ active: current?.user.id === item.user.id }"
          @click="openDetail(item)"
        >
          <div class="list-row">
            <span class="name">{{ item.user.displayName || '(未设昵称)' }}</span>
            <span class="cert-tag" :class="certStatusClass(item)">{{ certStatusText(item) }}</span>
          </div>
          <div class="list-sub">
            <span class="openid">{{ item.user.openid }}</span>
          </div>
          <div class="list-sub">
            <span v-if="item.latestCert">{{ item.latestCert.schoolName }} · {{ item.latestCert.enrollYear }}届</span>
            <span v-else class="muted">仅登录建号,尚未提交证件</span>
          </div>
          <div class="list-sub">
            <span>注册于 {{ formatTime(item.user.createdAt) }}</span>
          </div>
        </div>
      </div>

      <!-- 右:详情 -->
      <div class="detail">
        <div v-if="!current" class="detail-empty">
          <el-empty description="选一名用户查看详情" />
        </div>
        <template v-else>
          <div class="detail-header">
            <div class="title">{{ current.user.displayName || '(未设昵称)' }}</div>
            <div class="subtitle">用户 ID <code>{{ current.user.id }}</code></div>
            <div class="subtitle">openid <code>{{ current.user.openid }}</code></div>
            <div class="subtitle">
              账户状态 <el-tag size="small">{{ current.user.status }}</el-tag>
              · 注册于 {{ formatTime(current.user.createdAt) }}
              · 最近登录 {{ formatTime(current.user.lastLoginAt) }}
            </div>
          </div>

          <!-- 未提交证件 -->
          <div v-if="!current.latestCert" class="no-cert">
            <el-alert
              type="info"
              :closable="false"
              title="该用户尚未提交身份证件"
              description="用户已注册(微信登录建号),但还没有在小程序里提交学校证件,暂无可审核内容。"
            />
          </div>

          <!-- 有证件 -->
          <template v-else>
            <div class="cert-meta">
              <span class="cert-tag" :class="certStatusClass(current)">{{ certStatusText(current) }}</span>
              <span>{{ current.latestCert.certType }}</span>
              <span>·</span>
              <span>{{ current.latestCert.schoolName }} · {{ current.latestCert.enrollYear }}届</span>
              <span>·</span>
              <span>提交于 {{ formatTime(current.latestCert.createdAt) }}</span>
            </div>

            <div class="detail-images" v-loading="imgLoading">
              <div v-if="!imgLoading && imgUrls.length === 0" class="muted">图加载中...</div>
              <img
                v-for="(src, i) of imgUrls"
                :key="i"
                :src="src"
                class="cert-img"
                @click="openInNewTab(src)"
              />
            </div>

            <div v-if="current.latestCert.status === 'PENDING'" class="detail-actions">
              <el-button type="success" size="large" @click="onApprove">通过</el-button>
              <el-button type="danger" size="large" @click="onReject">驳回</el-button>
            </div>
            <div v-else class="detail-handled">
              <span v-if="current.latestCert.status === 'APPROVED'" class="tag-approved">✓ 已通过</span>
              <span v-else-if="current.latestCert.status === 'REJECTED'" class="tag-rejected">
                ✕ 已驳回:{{ current.latestCert.rejectReason }}
              </span>
              <div class="muted" v-if="current.latestCert.reviewedAt">
                处理时间:{{ formatTime(current.latestCert.reviewedAt) }}
              </div>
              <div class="muted" v-if="current.latestCert.status === 'REJECTED'">
                用户可在小程序端看到驳回原因并重新提交,重提后会回到「待审核」。
              </div>
            </div>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.audit-pane { display: flex; flex-direction: column; height: calc(100vh - 80px); }
.tabs { margin-bottom: 12px; }
.layout { display: flex; flex: 1; gap: 16px; overflow: hidden; }
.list { width: 360px; overflow-y: auto; background: #fff; border-radius: 8px; padding: 8px; }
.list-total { padding: 6px 12px; font-size: 12px; color: #86868b; }
.list-item { padding: 12px; border-radius: 6px; cursor: pointer; margin-bottom: 6px; border: 1px solid transparent; }
.list-item:hover { background: #f5f5f7; }
.list-item.active { background: #e6f4ff; border-color: #409eff; }
.list-row { display: flex; justify-content: space-between; align-items: center; font-weight: 600; }
.name { color: #1d1d1f; }
.list-sub { margin-top: 4px; font-size: 12px; color: #86868b; display: flex; gap: 6px; flex-wrap: wrap; }
.openid { font-family: monospace; }

.cert-tag { font-size: 12px; padding: 1px 8px; border-radius: 10px; font-weight: 500; }
.st-none { background: #f0f0f0; color: #86868b; }
.st-pending { background: #fff4e0; color: #b26a00; }
.st-approved { background: #e3f6e9; color: #1e7c3e; }
.st-rejected { background: #fdeaea; color: #b42020; }

.detail { flex: 1; overflow-y: auto; background: #fff; border-radius: 8px; padding: 24px; }
.detail-empty { height: 100%; display: flex; align-items: center; justify-content: center; }
.detail-header .title { font-size: 22px; font-weight: 600; }
.detail-header .subtitle { margin-top: 6px; color: #666; font-size: 13px; }
.detail-header .subtitle code { background: #f5f5f7; padding: 2px 6px; border-radius: 4px; font-size: 12px; }
.no-cert { margin-top: 24px; }
.cert-meta { margin-top: 20px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; font-size: 13px; color: #666; }
.detail-images { margin-top: 16px; display: flex; flex-wrap: wrap; gap: 16px; min-height: 120px; }
.cert-img { max-width: 320px; max-height: 240px; border-radius: 6px; border: 1px solid #e4e7ed; cursor: zoom-in; }
.detail-actions { margin-top: 32px; display: flex; gap: 16px; }
.detail-handled { margin-top: 24px; }
.tag-approved { color: #1e7c3e; font-size: 18px; font-weight: 600; }
.tag-rejected { color: #b42020; font-size: 16px; }
.muted { color: #86868b; font-size: 13px; margin-top: 6px; }
</style>

